import classNames from 'classnames';
import { BadgeWithText } from 'common/badge-with-text.js';
import { IconWithText } from 'common/icon/icon-with-text.js';
import { LinkFactoryReactRouter } from 'common/link/link-react-router.js';
import { Tabs } from 'common/tabs/tab.js';
import { TileBodyHeaderV2 } from 'common/tile/tile-body-header-v2.js';
import { Tile } from 'common/tile/tile.js';
import { IModule } from 'redux-state/layout/types.js';
import { getAccessStatus } from 'utilities/status/state-access.js';
import { StatusAccess } from 'utilities/status/status-access.js';
import { StatusRating } from 'utilities/status/status-rating.js';
import { setDefaultTitle } from 'utilities/text/titleDefault.js';

interface IItemStatus {
  name: string;
  status: StatusAccess;
}

export interface IFacility {
  facilityTypeName: string;
  items: IItemStatus[];
}

interface IFacilitiesStatus {
  status: {
    open: number;
    paused: number;
    closed: number;
  };
  facilityList: IFacility[];
  viewMoreUrl?: string;
}

function FacilityItems({ facility }: { facility: IFacility }) {
  const { items } = facility;

  return (
    <div className="u-pT-sm">
      {items.map((item: IItemStatus, idx: number) => (
        <FacilityItem item={item} key={idx} idx={idx} />
      ))}
    </div>
  );
}

export function FacilityItem({ item, idx }: { item: IItemStatus; idx: number }) {
  const status = getAccessStatus(item.status);
  const itemId = `status-${idx}`;

  return (
    <IconWithText icon={status.icon} key={idx} classes="u-mB-xs u-flex" contentClass="u-flex u-flexAlignItemsCenter">
      <small id={itemId} className="small u-mT-0 u-mB-0">
        {item.name}
      </small>
      {/* Display hidden, not hidden from screen readers */}
      <label htmlFor={itemId} hidden={true}>
        {status.label}
      </label>
    </IconWithText>
  );
}

export const FacilitiesStatus = ({ config, modulePath, isDrawer }: IModule<IFacilitiesStatus>) => {
  const {
    status: { open, paused, closed },
    facilityList = [],
    viewMoreUrl,
  } = config;

  // Filter out facilities that don't have any items
  const filteredFacilityList = facilityList.filter((facility: IFacility) => facility.items.length > 0);

  return (
    <Tile dataModule="facilities-status" config={config}>
      <div className="Tile-body">
        <TileBodyHeaderV2
          isDrawer={isDrawer}
          modulePath={modulePath}
          config={setDefaultTitle(config, 'Facilities status')}
        />

        <div className="u-flex u-flexJustifyBetween u-bB-1 u-pT-sm u-pB-sm">
          <Status count={open} label="Open" statusClass={StatusRating.statusGood} />
          <Status count={paused} label="On Hold" statusClass={StatusRating.statusMedium} />
          <Status count={closed} label="Closed" statusClass={StatusRating.statusBad} />
        </div>

        {filteredFacilityList.length > 0 && (
          <Tabs
            className="Tab--slim"
            headers={filteredFacilityList.map((facility) => facility.facilityTypeName)}
            renderActiveTab={(activeTabIndex: number) => (
              <FacilityItems facility={filteredFacilityList[activeTabIndex]} />
            )}
          />
        )}

        {viewMoreUrl && (
          <div className="u-mT-sm u-bT-1 u-pT-sm u-flex u-flexJustifyCenter u-flexAlignItemsCenter">
            <LinkFactoryReactRouter className="Link u-linkClean" href={viewMoreUrl}>
              <small className="u-textBold">View more facilities</small>
            </LinkFactoryReactRouter>
          </div>
        )}
      </div>
    </Tile>
  );
};

export function Label({ label, value }: { label: string; value: string }) {
  return (
    <>
      <h4 className="u-mT-sm u-mB-0">{label}</h4>
      <small>{value}</small>
    </>
  );
}

export function Status({ count, statusClass, label }: { count: number; label: string; statusClass: string }) {
  return (
    <BadgeWithText
      count={count}
      classes={classNames('BadgeWithText--large u-textWhite u-flexAlignItemsCenter', statusClass)}
    >
      <small className="u-textDefault">{label}</small>
    </BadgeWithText>
  );
}
